/* eslint-disable @typescript-eslint/no-unsafe-return */

import {Component, Host, Prop, h, JSX, Element, forceUpdate, State} from "@stencil/core";
import {IUserSettingsAsset, STAGE} from "@trumpf/notifies.user-settings-handler";
import {i18nProvider} from "@trumpf/ux.i18n";
import {translations} from "./i18n";

import {AuthSubscribe, AuthUnsubscribe} from "../auth/auth";

import {store, getUserSettings, sendUserSettings, loadMoreAssets, getInitialAssets} from "../stores/user-settings-handler-store";
import {CancelEditDialog, FormWrapper, Topic, SettingsFooter} from "./notification-settings.sub";
import {AssetsSection} from "./AssetsSection.sub";
import {ChannelsSection} from "./ChannelsSection.sub";

//! get rid of this and include it in the category object
enum Channels {
	eMail = "eMailChannel",
	notification = "notificationChannel",
}

const {i18n} = i18nProvider;

/**
 * Notification settings component to manage user notification settings.
 * @slot greeter_headline - default greeter headline
 * @slot greeter_text - default greeter text
 *
 * @slot channels_categories_headline - channels specific headline
 * @slot channels_categories_text - channels specific text
 *
 * @slot category_table_heading - category column header
 * @slot notification_channel_table_heading - channel column header
 * @slot email_channel_table_heading - email column header
 *
 * @slot assets_headline - assets specific headline
 * @slot assets_text - assets specific text
 *
 * @slot assets_table_heading - assets column header
 *
 * @slot save_button_label - save-button label
 * @slot cancel_button_label - cancel-button label
 *
 * @slot cancel_modal_headline - cancel-dialog headline
 * @slot cancel_confirm_label - cancel-confirm-button label
 * @slot cancel_reject_label - cancel-reject-button label
 * @slot cancel_reject_label - cancel-reject-button label
 */
@Component({
	tag: "uxn-notification-settings",
	styleUrl: "notification-settings.scss",
	shadow: false,
})
export class NotificationSettings {
	@Element() el: HTMLUxnNotificationSettingsElement;

	/** the url to the main notification center */
	@Prop() notificationsCenterUrl: string = ""; //! DEAD
	/** content of notifications tooltip */
	@Prop() notificationsTooltip: string = "";
	/** locale */
	@Prop() locale: string = "de_DE";
	/** content of email tooltip */
	@Prop() emailTooltip: string = "";
	/** backend api version  */
	@Prop() version: string = "2.0";
	/** environment for fetching data  */
	@Prop() env: string = "prod";
	/** action attribute for the form */
	@Prop() formAction: string = "";
	/** id attribute of the form  */
	@Prop() formId: string = crypto.randomUUID();
	/** debugging */
	@Prop() debug: boolean = false;

	#authSubscription: number | null = null;
	#i18nSubscriberId: string;

	@State() assetsFilter: string = "";
	#updateAssetsFilter = (value: string) => (this.assetsFilter = value);
	@State() isAllAssetsChecked: boolean = false;
	#updateIsAllAssetsChecked = (value: boolean) => (this.isAllAssetsChecked = value);

	async componentWillLoad() {
		this.#authSubscription = await AuthSubscribe(this.version, this.env, this.locale, this.debug);

		this.#customTranslation();
		await i18nProvider.changeLanguage(this.locale || "de_DE");
		this.#i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));

		store.onChange("assets", () => {
			const infiniteScrollSensor = this.el.querySelector<HTMLElement>("ux-infinite-scroll-sensor");
			if (infiniteScrollSensor) infiniteScrollSensor.setAttribute("has-fired", "false");
		});
	}

	disconnectedCallback() {
		i18nProvider.unsubscribe(this.#i18nSubscriberId);
		store.set("token", null);

		if (this.#authSubscription === null) return;
		AuthUnsubscribe(this.#authSubscription);
		this.#authSubscription = null;
	}

	#customTranslation() {
		Object.entries(translations).forEach(([language, bundle]) => i18n.addResourceBundle(language, "user-settings", bundle));

		i18n.on("languageChanged", (lng) => {
			if (Object.keys(translations).includes(lng)) return;

			const lngCode = lng.split("_")[0];
			const fallbackLng = Object.keys(translations).find((key) => key.startsWith(lngCode));
			if (fallbackLng) i18n.changeLanguage(fallbackLng);
		});
	}

	#requestUserSettings = () => {
		const token = store.get("token");
		if (!token) return;
		getUserSettings(token, this.locale, this.version, STAGE[this.env], () => getInitialAssets());
	};

	#getStatusIcon = (): JSX.Element | null => {
		if (store.get("loading")) return <ux-icon name="arrow-rotate-bottom" class="ux-color-warning" sizeL="20" weight="bold" />;
		if (store.get("error") !== null) return <ux-icon name="cross" class="ux-color-error" sizeL="20" weight="bold" />;
		if (store.get("unsavedChanges")) return <ux-icon name="octagon-warning" class="ux-color-info" sizeL="20" weight="bold" />;
		if (store.get("saved")) return <ux-icon name="checkmark" class="ux-color-success" sizeL="20" weight="bold" />;
		return null;
	};

	#updateChannel = (category: string, channel: string, enabled: boolean) => {
		const userSettings = store.get("userSettings");
		const categories = [...userSettings.categories];

		categories.find((_category) => _category.name === category).channels.find((_channel) => _channel.name === channel).enabled = enabled;

		store.set("userSettings", {...userSettings, categories});
		store.set("unsavedChanges", true); //! move that into the store (and keeping track of changes)
	};

	#updateAssets = (id: string | string[], checked: boolean) => {
		const userSettings = store.get("userSettings");
		let assets: IUserSettingsAsset[];

		if (Array.isArray(id)) {
			const rawAssets = store.get("assets");
			const filteredAssets = rawAssets.filter(({assetId}) => id.includes(assetId));
			assets = filteredAssets.map(({assetId}) => ({
				id: assetId,
				enabled: checked,
			}));
		} else {
			// update asset or push new element
			assets = userSettings.assets.map((asset) => (asset.id === id ? {...asset, enabled: checked} : asset));
			if (!assets.some((asset) => asset.id === id)) assets.push({id, enabled: checked});
		}

		this.#updateIsAllAssetsChecked(assets.every((asset) => asset.enabled));

		store.set("userSettings", {...userSettings, assets});
		store.set("unsavedChanges", true);
	};

	#submitSettings = (e: Event) => {
		e.preventDefault();
		e.stopImmediatePropagation();
		e.stopPropagation();

		this.#updateAssetsFilter("");

		const token = store.get("token");
		if (!token) return;
		sendUserSettings(token, this.version, store.get("userSettings"), STAGE[this.env]);
	};

	render() {
		return (
			<Host class="uxn-notification-settings gr-8 push-2 no-gutter gr-10@t push-1@t gr-10@m push-1@m no-gutter@m">
				<Topic headlineSlot="greeter_headline" textSlot="greeter_text" />
				<Topic headlineSlot="headline_1" textSlot="text_1" />
				<FormWrapper id={this.formId} action={this.formAction} onSubmit={this.#submitSettings}>
					<ChannelsSection
						topic={{headlineSlot: "channels_categories_headline", textSlot: "channels_categories_text"}}
						table={{
							categoryHeaderSlot: "category_table_heading",
							notificationChannel: {headerSlot: "notification_channel_table_heading", tooltip: this.notificationsTooltip},
							eMailChannel: {headerSlot: "email_channel_table_heading", tooltip: this.emailTooltip},
						}}
						isLoading={store.get("loading")}
						categories={store.get("categories") || []} //! Ensure store.get("categories") returns an empty array instead of null; get rid of || [] here
						userSettingsCategories={store.get("userSettings")?.categories || []} //! Ensure store.get("userSettings").categories returns an empty array instead of null; get rid of || [] here
						updateChannel={this.#updateChannel}
					/>
					<AssetsSection
						topic={{headlineSlot: "assets_headline", textSlot: "assets_text"}}
						table={{headerSlot: "assets_table_heading"}}
						isLoading={store.get("loading")}
						assets={store.get("assets")}
						userSettingsAssets={store.get("userSettings")?.assets || []} //! Ensure store.get("userSettings").assets returns an empty array instead of null; get rid of || [] here
						loadMoreAssets={loadMoreAssets}
						updateAssets={this.#updateAssets}
						assetsFilter={this.assetsFilter}
						updateAssetsFilter={this.#updateAssetsFilter}
					/>
					<SettingsFooter
						saveButtonSlot="save_button_label"
						cancelButtonSlot="cancel_button_label"
						hidden={store.get("loading")}
						unsavedChanges={store.get("unsavedChanges")}
						statusIcon={this.#getStatusIcon()}
					/>
				</FormWrapper>
				<CancelEditDialog
					headlineSlot="cancel_modal_headline"
					cancelEditButtonSlot="cancel_confirm_label"
					continueEditButtonSlot="cancel_reject_label"
					onCancelEdit={(e) => {
						e.preventDefault();
						this.#updateAssetsFilter("");
						const token = store.get("token");
						store.reset();
						store.set("token", token);
						this.#requestUserSettings();
					}}
				/>
			</Host>
		);
	}
}
